import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Typography
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

export interface ColumnOption {
  key: string;
  label: string;
  required?: boolean;
}

interface ColumnVisibilityMenuProps {
  columns: ColumnOption[];
  visible: Set<string>;
  onToggle: (key: string) => void;
}

const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  columns,
  visible,
  onToggle
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const optional = columns.filter((c) => !c.required);
  const shown = columns.filter((c) => c.required || visible.has(c.key)).length;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Columns ({shown}/{columns.length})
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, maxHeight: 460, p: 0 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Show/Hide Columns
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select which columns to display in the table
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1.5, py: 1, overflowY: 'auto', maxHeight: 360 }}>
          {columns.map((col) => (
            <FormControlLabel
              key={col.key}
              sx={{ display: 'flex', mx: 0, my: 0.25 }}
              control={
                <Checkbox
                  size="small"
                  checked={col.required || visible.has(col.key)}
                  disabled={col.required}
                  onChange={() => onToggle(col.key)}
                />
              }
              label={
                <Typography variant="body2">
                  {col.label}
                  {col.required ? ' (Required)' : ''}
                </Typography>
              }
            />
          ))}
          {optional.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No optional columns
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ColumnVisibilityMenu;
